import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Slide from "@mui/material/Slide";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PublishRoundedIcon from "@mui/icons-material/PublishRounded";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import React from "react";
import {
    fetchAwarenessCampaigns,
    deleteAwarenessCampaign,
    publishAwarenessCampaign,
} from "../vsecuriteSlice";
import { showErrorToast, showSuccessToast } from "../../../app/utils/helpers";
import Langs, { Localize } from "../../../app/lang/langs";

const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * AwarenessList — Admin table of all awareness campaigns.
 *
 * Props:
 *   onSelectCampaign(campaign) — called when the admin clicks "Voir suivi" on a published campaign
 *   onEditCampaign(campaign)   — called when the admin clicks "Modifier" on a draft campaign
 *   onCreateNew()              — called when the admin clicks "Nouvelle Campagne"
 */
const AwarenessList = ({ onSelectCampaign, onEditCampaign, onCreateNew }) => {
    const dispatch = useDispatch();

    /** Redux state */
    const campaigns = useSelector((state) => state.vsecurite.awareness.campaigns);
    const loading = useSelector((state) => state.vsecurite.awareness.loading);
    const lang = useSelector((state) => state.settings.lang);
    const isMobile = useSelector((state) => state.settings.isMobile);

    const mobile = isMobile === 2;

    const [search, setSearch] = useState("");

    const filteredCampaigns = (campaigns || []).filter((c) => {
        const q = search.toLowerCase();
        return !q || (c.title || "").toLowerCase().includes(q) || (c.status || "").toLowerCase().includes(q);
    });

    /** Confirmation dialog state */
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: "",
        message: "",
        onConfirm: null,
    });

    useEffect(() => {
        dispatch(fetchAwarenessCampaigns());
    }, [dispatch]);

    /**
     * Opens the confirmation dialog with the provided options.
     * @param {string} title   - Dialog heading
     * @param {string} message - Body text explaining the action
     * @param {Function} onConfirm - Callback executed when the user confirms
     */
    const openConfirm = (title, message, onConfirm) => {
        setConfirmDialog({ open: true, title, message, onConfirm });
    };

    /** Closes the confirmation dialog without executing any action. */
    const closeConfirm = () => {
        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
    };

    /**
     * Handles the "Publier" action for a draft campaign.
     * Asks for confirmation, then dispatches publishAwarenessCampaign.
     * @param {Object} campaign - The campaign row object
     */
    const handlePublish = (campaign) => {
        openConfirm(
            Localize("VSEC_PUBLISH_CAMPAIGN_TITLE", lang),
            `${Localize("VSEC_PUBLISH_CAMPAIGN_MSG", lang)} "${campaign.title}" ${Localize("VSEC_PUBLISH_CAMPAIGN_MSG2", lang)}`,
            async () => {
                closeConfirm();
                try {
                    await dispatch(publishAwarenessCampaign({ id: campaign.id })).unwrap();
                    showSuccessToast(Localize("VSEC_TOAST_CAMPAIGN_PUBLISHED", lang));
                } catch {
                    showErrorToast(Localize("VSEC_TOAST_CAMPAIGN_PUBLISH_ERROR", lang));
                }
            }
        );
    };

    /**
     * Handles the "Supprimer" action for any campaign.
     * Asks for confirmation, then dispatches deleteAwarenessCampaign.
     * @param {Object} campaign - The campaign row object
     */
    const handleDelete = (campaign) => {
        openConfirm(
            Localize("VSEC_DELETE_CAMPAIGN_TITLE", lang),
            `${Localize("VSEC_DELETE_CAMPAIGN_MSG", lang)} "${campaign.title}" ${Localize("VSEC_DELETE_CAMPAIGN_MSG2", lang)}`,
            async () => {
                closeConfirm();
                try {
                    await dispatch(deleteAwarenessCampaign({ id: campaign.id })).unwrap();
                    showSuccessToast(Localize("VSEC_TOAST_CAMPAIGN_DELETED", lang));
                } catch {
                    showErrorToast(Localize("VSEC_TOAST_CAMPAIGN_DELETE_ERROR", lang));
                }
            }
        );
    };

    /**
     * Returns a MUI Chip with the correct label and color for a given status value.
     * draft      → grey
     * published  → green
     * archived   → orange (#FF9015)
     * @param {Object} rowData - DataTable row
     */
    const statusBodyTemplate = (rowData) => {
        const statusMap = {
            draft: { label: Localize("VSEC_STATUS_DRAFT_CHIP", lang), bgcolor: "rgba(100,116,139,0.12)", color: "rgba(71,85,105,0.9)" },
            published: { label: Localize("VSEC_STATUS_PUBLISHED_CHIP", lang), bgcolor: "rgba(34,197,94,0.12)", color: "rgb(22,163,74)" },
            archived: { label: Localize("VSEC_STATUS_ARCHIVED_CHIP", lang), bgcolor: "rgba(255,144,21,0.12)", color: "#FF9015" },
        };
        const cfg = statusMap[rowData.status] || statusMap["draft"];
        return (
            <Chip
                label={cfg.label}
                size="small"
                sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    height: 24,
                    borderRadius: "8px",
                    bgcolor: cfg.bgcolor,
                    color: cfg.color,
                }}
            />
        );
    };

    /**
     * Formats a date value as DD-MM-YYYY.
     * Returns "—" when no value is present.
     * @param {*} val - The raw date value from the cell
     */
    const renderDate = (val) => {
        return val ? <span>{moment(val).format("DD-MM-YYYY")}</span> : <span style={{ color: "rgba(0,0,0,0.3)" }}>—</span>;
    };

    /**
     * Renders the action buttons for a row.
     * - "Voir suivi"  visible only when statut === 'published'
     * - "Modifier"    visible only when statut === 'draft'
     * - "Publier"     visible only when statut === 'draft'
     * - "Supprimer"   always visible
     * @param {Object} rowData - DataTable row
     */
    const actionsBodyTemplate = (rowData) => {
        const isDraft = rowData.status === "draft";
        const isPublished = rowData.status === "published";

        return (
            <Stack direction="row" spacing={0.5} alignItems="center">
                {/* View tracking — published only */}
                {isPublished && (
                    <Tooltip title={Localize("VSEC_TOOLTIP_VIEW_TRACKING", lang)}>
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onSelectCampaign && onSelectCampaign(rowData); }}
                            sx={{
                                color: "rgb(8,137,193)",
                                minWidth: 44,
                                minHeight: 44,
                                "&:hover": { bgcolor: "rgba(8,137,193,0.1)" },
                            }}
                        >
                            <VisibilityRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Edit — draft only */}
                {isDraft && (
                    <Tooltip title={Localize("VSEC_TOOLTIP_EDIT_CAMPAIGN", lang)}>
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onEditCampaign && onEditCampaign(rowData); }}
                            sx={{
                                color: "rgba(71,85,105,0.8)",
                                minWidth: 44,
                                minHeight: 44,
                                "&:hover": { bgcolor: "rgba(71,85,105,0.1)" },
                            }}
                        >
                            <EditRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Publish — draft only */}
                {isDraft && (
                    <Tooltip title={Localize("VSEC_TOOLTIP_PUBLISH_CAMPAIGN", lang)}>
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handlePublish(rowData); }}
                            sx={{
                                color: "rgb(22,163,74)",
                                minWidth: 44,
                                minHeight: 44,
                                "&:hover": { bgcolor: "rgba(22,163,74,0.1)" },
                            }}
                        >
                            <PublishRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Delete — always */}
                <Tooltip title={Localize("VSEC_TOOLTIP_DELETE_CAMPAIGN", lang)}>
                    <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleDelete(rowData); }}
                        sx={{
                            color: "rgb(220,38,38)",
                            minWidth: 44,
                            minHeight: 44,
                            "&:hover": { bgcolor: "rgba(220,38,38,0.1)" },
                        }}
                    >
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Stack>
        );
    };

    /* ── Mobile card for a single campaign ── */
    const renderMobileCard = (campaign, idx) => {
        const isDraft = campaign.status === "draft";
        const isPublished = campaign.status === "published";

        return (
            <Card
                key={campaign.id}
                elevation={0}
                sx={{
                    borderRadius: "14px",
                    border: "1px solid rgba(0,40,128,0.06)",
                    background: "rgba(255,255,255,0.85)",
                    mb: 1.5,
                    transition: `all 300ms ${EASE}`,
                    "@keyframes cardFadeIn": {
                        from: { opacity: 0, transform: "translateY(10px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: `cardFadeIn 350ms ${EASE} ${idx * 60}ms both`,
                    "&:active": { transform: "scale(0.98)" },
                }}
            >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    {/* Title + Status */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography
                            sx={{
                                fontWeight: 650,
                                fontSize: "0.95rem",
                                color: "rgba(0,40,128,0.85)",
                                flex: 1,
                                mr: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {campaign.title}
                        </Typography>
                        {statusBodyTemplate(campaign)}
                    </Stack>

                    {/* Dates */}
                    <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
                        <Box>
                            <Typography sx={{ fontSize: "0.7rem", color: "rgba(0,0,0,0.4)", fontWeight: 500 }}>
                                <Langs str="VSEC_COL_CREATED_AT" />
                            </Typography>
                            <Typography sx={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.7)" }}>
                                {campaign.created_at ? moment(campaign.created_at).format("DD-MM-YYYY") : "—"}
                            </Typography>
                        </Box>
                        {campaign.published_at && (
                            <Box>
                                <Typography sx={{ fontSize: "0.7rem", color: "rgba(0,0,0,0.4)", fontWeight: 500 }}>
                                    <Langs str="VSEC_COL_PUBLISH_DATE" />
                                </Typography>
                                <Typography sx={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.7)" }}>
                                    {moment(campaign.published_at).format("DD-MM-YYYY")}
                                </Typography>
                            </Box>
                        )}
                    </Stack>

                    <Divider sx={{ borderColor: "rgba(0,40,128,0.04)", mb: 1.5 }} />

                    {/* Actions */}
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {isPublished && (
                            <IconButton
                                onClick={() => onSelectCampaign && onSelectCampaign(campaign)}
                                sx={{
                                    color: "rgb(8,137,193)",
                                    minWidth: 44,
                                    minHeight: 44,
                                    "&:hover": { bgcolor: "rgba(8,137,193,0.1)" },
                                }}
                            >
                                <VisibilityRoundedIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        )}
                        {isDraft && (
                            <IconButton
                                onClick={() => onEditCampaign && onEditCampaign(campaign)}
                                sx={{
                                    color: "rgba(71,85,105,0.8)",
                                    minWidth: 44,
                                    minHeight: 44,
                                    "&:hover": { bgcolor: "rgba(71,85,105,0.1)" },
                                }}
                            >
                                <EditRoundedIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        )}
                        {isDraft && (
                            <IconButton
                                onClick={() => handlePublish(campaign)}
                                sx={{
                                    color: "rgb(22,163,74)",
                                    minWidth: 44,
                                    minHeight: 44,
                                    "&:hover": { bgcolor: "rgba(22,163,74,0.1)" },
                                }}
                            >
                                <PublishRoundedIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        )}
                        <IconButton
                            onClick={() => handleDelete(campaign)}
                            sx={{
                                color: "rgb(220,38,38)",
                                minWidth: 44,
                                minHeight: 44,
                                "&:hover": { bgcolor: "rgba(220,38,38,0.1)" },
                            }}
                        >
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    return (
        <Box sx={{ p: 0, mt: 0 }}>
            {/* ------------------------------------------------------------------ */}
            {/* Header: title + "Nouvelle Campagne" button                         */}
            {/* ------------------------------------------------------------------ */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                    px: 1,
                    flexDirection: mobile ? "column" : "row",
                    gap: mobile ? 1.5 : 0,
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: "rgba(0,40,128,0.85)",
                        fontSize: "1.1rem",
                        letterSpacing: "-0.02em",
                        alignSelf: mobile ? "flex-start" : "center",
                    }}
                >
                    <Langs str="VSEC_CAMPAIGNS_TITLE" />
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    onClick={onCreateNew}
                    fullWidth={mobile}
                    sx={{
                        bgcolor: "#FF9015",
                        color: "#fff",
                        fontWeight: 600,
                        borderRadius: "12px",
                        textTransform: "none",
                        minHeight: 48,
                        boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                        transition: `all 250ms ${EASE}`,
                        "&:hover": {
                            bgcolor: "#e07d0a",
                            boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
                            transform: "translateY(-1px) scale(1.02)",
                        },
                        "&:active": { transform: "scale(0.98)" },
                    }}
                >
                    <Langs str="VSEC_BTN_NEW_CAMPAIGN" />
                </Button>
            </Box>

            {/* ------------------------------------------------------------------ */}
            {/* Table card                                                          */}
            {/* ------------------------------------------------------------------ */}
            <Box
                sx={{
                    background: "rgba(255,255,255,0.75)",
                    backdropFilter: "blur(14px)",
                    WebkitBackdropFilter: "blur(14px)",
                    borderRadius: mobile ? "14px" : "18px",
                    border: "1px solid rgba(0,40,128,0.06)",
                    boxShadow: "0 1px 4px rgba(255,144,21,0.04), 0 8px 32px rgba(0,40,128,0.04)",
                    overflow: "hidden",
                    position: "relative",
                    minHeight: 200,
                    p: mobile ? 1.5 : 2.5,
                    transition: `box-shadow 300ms ${EASE}`,
                    "@keyframes listFadeIn": {
                        from: { opacity: 0, transform: "translateY(10px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: `listFadeIn 400ms ${EASE} both`,
                }}
            >
                {/* Loading overlay */}
                {loading && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgba(255,255,255,0.7)",
                            zIndex: 10,
                            borderRadius: "14px",
                        }}
                    >
                        <CircularProgress sx={{ color: "#FF9015" }} size={36} />
                    </Box>
                )}

                {/* Search field */}
                <Box sx={{ mb: 2 }}>
                    <TextField
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={Localize("VSEC_SEARCH_PLACEHOLDER", lang)}
                        fullWidth={mobile}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: "#888" }} />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{
                            width: mobile ? "100%" : 280,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                background: "rgba(0,40,128,0.025)",
                                transition: `all 250ms ${EASE}`,
                                "&.Mui-focused": {
                                    background: "rgba(255,255,255,0.9)",
                                    boxShadow: "0 0 0 3px rgba(255,144,21,0.15)",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#FF9015",
                                },
                            },
                            "& input": { fontSize: "16px" },
                        }}
                    />
                </Box>

                {/* Desktop: DataGrid */}
                {!mobile && (
                    <DataGrid
                        rows={filteredCampaigns}
                        getRowId={(row) => row.id}
                        disableRowSelectionOnClick
                        columns={[
                            {
                                field: "title",
                                headerName: Localize("VSEC_COL_TITLE", lang),
                                minWidth: 220,
                                flex: 2,
                                sortable: true,
                            },
                            {
                                field: "status",
                                headerName: Localize("VSEC_COL_STATUS", lang),
                                minWidth: 120,
                                flex: 1,
                                sortable: true,
                                renderCell: (params) => statusBodyTemplate(params.row),
                            },
                            {
                                field: "published_at",
                                headerName: Localize("VSEC_COL_PUBLISH_DATE", lang),
                                minWidth: 140,
                                flex: 1,
                                sortable: true,
                                renderCell: (params) => renderDate(params.row.published_at),
                            },
                            {
                                field: "created_at",
                                headerName: Localize("VSEC_COL_CREATED_AT", lang),
                                minWidth: 140,
                                flex: 1,
                                sortable: true,
                                renderCell: (params) => renderDate(params.row.created_at),
                            },
                            {
                                field: "_actions",
                                headerName: Localize("VSEC_COL_ACTIONS", lang),
                                minWidth: 150,
                                flex: 1,
                                sortable: false,
                                filterable: false,
                                renderCell: (params) => actionsBodyTemplate(params.row),
                            },
                        ]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 25 } },
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        localeText={{
                            noRowsLabel: Localize("VSEC_EMPTY_CAMPAIGNS", lang),
                        }}
                        sx={{
                            height: 'calc(100vh - 320px)',
                            borderRadius: "14px",
                            border: "1px solid rgba(0,40,128,0.06)",
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "rgba(247,248,252,0.9)",
                                fontWeight: 700,
                            },
                            "& .MuiDataGrid-row": {
                                transition: `background-color 200ms ${EASE}`,
                            },
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "rgba(255,144,21,0.05)",
                            },
                            "& .MuiDataGrid-row:nth-of-type(even)": {
                                backgroundColor: "rgba(0,40,128,0.015)",
                            },
                            "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                                borderBottom: "1px solid rgba(0,40,128,0.04)",
                            },
                        }}
                    />
                )}

                {/* Mobile: Card list */}
                {mobile && (
                    <Box sx={{ overflowY: "auto", maxHeight: "calc(100vh - 320px)" }}>
                        {filteredCampaigns.length === 0 && !loading && (
                            <Box sx={{ textAlign: "center", py: 6 }}>
                                <Typography sx={{ color: "rgba(0,0,0,0.4)", fontSize: "0.9rem" }}>
                                    <Langs str="VSEC_EMPTY_CAMPAIGNS" />
                                </Typography>
                            </Box>
                        )}
                        {filteredCampaigns.map((c, idx) => renderMobileCard(c, idx))}
                    </Box>
                )}
            </Box>

            {/* ------------------------------------------------------------------ */}
            {/* Confirmation dialog (publish / delete)                             */}
            {/* ------------------------------------------------------------------ */}
            <Dialog
                open={confirmDialog.open}
                onClose={closeConfirm}
                fullScreen={mobile}
                TransitionComponent={mobile ? SlideUp : undefined}
                sx={{ zIndex: 1400 }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: mobile ? 0 : "18px",
                            bgcolor: "#fff",
                            color: "rgba(30,30,30,0.9)",
                            minWidth: mobile ? "100%" : 360,
                            boxShadow: mobile ? "none" : "0 16px 48px rgba(0,40,128,0.12), 0 4px 16px rgba(255,144,21,0.06)",
                        },
                    },
                }}
            >
                {mobile && (
                    <AppBar
                        position="sticky"
                        elevation={0}
                        sx={{
                            bgcolor: "#fff",
                            color: "rgba(30,30,30,0.9)",
                            borderBottom: "1px solid rgba(0,40,128,0.06)",
                        }}
                    >
                        <Toolbar>
                            <IconButton edge="start" onClick={closeConfirm} sx={{ color: "rgba(0,40,128,0.7)" }}>
                                <CloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 1, fontWeight: 700, fontSize: "1rem", flex: 1 }}>
                                {confirmDialog.title}
                            </Typography>
                        </Toolbar>
                    </AppBar>
                )}
                {!mobile && (
                    <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 1 }}>
                        {confirmDialog.title}
                    </DialogTitle>
                )}
                <DialogContent sx={{ px: 3, pt: mobile ? 3 : undefined }}>
                    <DialogContentText sx={{ fontSize: "0.9rem", color: "rgba(30,30,30,0.7)" }}>
                        {confirmDialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: 3,
                        pb: 2.5,
                        gap: 1,
                        ...(mobile && {
                            position: "sticky",
                            bottom: 0,
                            bgcolor: "#fff",
                            borderTop: "1px solid rgba(0,40,128,0.06)",
                            flexDirection: "column",
                            p: 2,
                        }),
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={closeConfirm}
                        fullWidth={mobile}
                        sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            minHeight: 48,
                            borderColor: "rgba(0,40,128,0.15)",
                            color: "rgba(0,40,128,0.7)",
                            transition: `all 250ms ${EASE}`,
                            "&:hover": {
                                borderColor: "rgba(0,40,128,0.35)",
                                bgcolor: "rgba(0,40,128,0.04)",
                                transform: "scale(1.02)",
                            },
                            "&:active": { transform: "scale(0.98)" },
                        }}
                    >
                        <Langs str="VSEC_BTN_CANCEL" />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => confirmDialog.onConfirm && confirmDialog.onConfirm()}
                        fullWidth={mobile}
                        sx={{
                            bgcolor: "#FF9015",
                            color: "#fff",
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            minHeight: 48,
                            boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                            transition: `all 250ms ${EASE}`,
                            "&:hover": {
                                bgcolor: "#e07d0a",
                                boxShadow: "0 4px 16px rgba(255,144,21,0.35)",
                                transform: "scale(1.02)",
                            },
                            "&:active": { transform: "scale(0.98)" },
                        }}
                    >
                        <Langs str="VSEC_BTN_CONFIRM" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AwarenessList;
